import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, DollarSign, TrendingUp, FileText, Building2, Send } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import EntityComms from "../components/shared/EntityComms";
import { Entity360Shell, FundingProgressBar } from "../components/shared/OSComponents";
import { fmtUSD } from "../utils/currency";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };

export default function Commitment360Page({ commitmentId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/commitment/${commitmentId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [commitmentId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Commitment not found</div>;

  const { commitment, investor, opportunity, deal, distributions, total_distributed } = data;
  const crumbs = [...breadcrumbs, { type: "commitment", id: commitmentId, label: `Commitment ${commitmentId.slice(0, 8)}` }];

  const relatedEntities = [
    ...(investor ? [{ type: "investor", id: investor.user_id, title: investor.full_name, subtitle: investor.email }] : []),
    ...(opportunity ? [{ type: "opportunity", id: commitment.opportunity_id, title: `${LOAN_LABELS[opportunity.loan_type] || "Opportunity"} — ${opportunity.state || ""}`, subtitle: `${fmtUSD(opportunity.target_raise)} target` }] : []),
    ...(deal ? [{ type: "deal", id: deal.lead_id, title: deal.full_name || `${deal.loan_type} Deal`, subtitle: deal.stage?.replace(/_/g, ' ') }] : []),
  ];

  const quickActions = [
    ...(deal ? [{ id: "view_deal", label: "View Parent Deal", icon: Building2, onClick: () => onNavigate("deal", deal.lead_id) }] : []),
    ...(investor ? [{ id: "view_investor", label: "View Investor", icon: DollarSign, onClick: () => onNavigate("investor", investor.user_id) }] : []),
    { id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") },
  ];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "distributions", label: `Distributions (${distributions.length})` },
    { key: "comms", label: "Communications" },
    { key: "notes", label: "Notes" },
  ];

  const kpis = [
    { label: "Committed", value: fmtUSD(commitment.amount), icon: DollarSign, accent: true },
    { label: "Distributed", value: fmtUSD(total_distributed), icon: TrendingUp },
    ...(opportunity ? [
      { label: "Yield", value: opportunity.projected_yield ? `${opportunity.projected_yield}%` : "—", icon: TrendingUp },
      { label: "Term", value: opportunity.projected_term_months ? `${opportunity.projected_term_months}mo` : "—", icon: Building2 },
    ] : []),
  ];

  const badges = [
    ...(commitment.express_commit ? [{ label: "Express", className: "bg-amber-100 text-amber-600 border border-amber-200" }] : []),
  ];

  return (
    <Entity360Shell type="commitment"
      title={`Commitment #${commitmentId.slice(0, 8)}`}
      subtitle={[investor?.full_name, commitment.created_at && new Date(commitment.created_at).toLocaleString()].filter(Boolean).join(" | ")}
      status={commitment.status} badges={badges} kpis={kpis}
      tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} relatedEntities={relatedEntities}>

      {tab === "overview" && (
        <div className="grid gap-4 md:grid-cols-3">
          {investor && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Investor</h3>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center text-sm font-bold text-emerald-700">{investor.full_name?.charAt(0)}</div>
                <div><EntityLink type="investor" id={investor.user_id} label={investor.full_name} onClick={navigateToEntity} />{investor.email && <p className="text-[10px] text-[#999]">{investor.email}</p>}</div>
              </div>
            </div>
          )}
          {opportunity && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Opportunity</h3>
              <EntityLink type="opportunity" id={commitment.opportunity_id} label={`${LOAN_LABELS[opportunity.loan_type] || opportunity.loan_type} — ${opportunity.state}`} onClick={navigateToEntity} />
              <div className="mt-2"><FundingProgressBar raised={opportunity.raised || 0} target={opportunity.target_raise} /></div>
            </div>
          )}
          {deal && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Parent Deal</h3>
              <EntityLink type="deal" id={deal.lead_id} label={deal.full_name || `${deal.loan_type} Deal`} onClick={navigateToEntity} />
              <div className="mt-1 text-[10px] text-[#999]">Stage: {deal.stage?.replace(/_/g, ' ')}</div>
            </div>
          )}
        </div>
      )}

      {tab === "distributions" && (
        <div className="space-y-2">
          {distributions.length === 0 ? (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-8 text-center text-sm text-[#888]">No distributions yet.</div>
          ) : distributions.map((d, i) => (
            <div key={`dist-${i}`} className="rounded-xl border border-[#e8e0d0] bg-white p-3 flex items-center justify-between">
              <span className="text-xs font-bold text-emerald-600">{fmtUSD(d.amount)}</span>
              <div className="flex gap-2 text-[10px] text-[#999]">{d.dist_type && <span>{d.dist_type}</span>}<span>{d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}</span></div>
            </div>
          ))}
        </div>
      )}

      {tab === "comms" && <EntityComms entityType="commitment" entityId={commitmentId} />}
      {tab === "notes" && <EntityNotes entityType="commitment" entityId={commitmentId} />}
    </Entity360Shell>
  );
}
